// The Part 2 room features effective for one session (contract §2).
//
// game_session_features returns each flag's value OR'd with "this is a test
// session", so staging's test games get everything while production stays
// dark. Read on mount, on focus, every 60 seconds while the page is visible,
// and on demand after any P0501 (the feature was switched off mid-game).
//
// Until the first answer lands, and whenever the read fails, every feature is
// OFF. The safe default for a new control is "not there".
import { useCallback, useEffect, useRef, useState } from 'react';
// The fetch and the flag list are Lane F's shared module (utils/sessionFeatures).
import {
  ROOM_FEATURE_FLAGS,
  ROOM_FEATURES_OFF,
  ROOM_FEATURES_REFRESH_MS,
  fetchRoomFeatures,
} from '../utils/sessionFeatures';

export const ROOM_FEATURES = ROOM_FEATURE_FLAGS;
export const FEATURES_OFF = ROOM_FEATURES_OFF;
export const FEATURE_REFRESH_MS = ROOM_FEATURES_REFRESH_MS;
const FOCUS_THROTTLE_MS = 5_000;

/** Coerce an RPC answer into the full flag map; anything missing is off. */
export function normalizeFeatures(raw) {
  const out = { ...FEATURES_OFF };
  if (raw && typeof raw === 'object') {
    for (const k of ROOM_FEATURES) out[k] = raw[k] === true;
  }
  return out;
}

/**
 * @param {string|null} sessionCode
 * @param {{enabled?: boolean}} [opts] false stops every read (no seat, TV, …)
 * @returns {{features: object, loaded: boolean, refresh: () => Promise<void>}}
 */
export default function useSessionFeatures(sessionCode, { enabled = true } = {}) {
  const [state, setState] = useState({ code: null, features: FEATURES_OFF, loaded: false, good: false });
  const lastAtRef = useRef(0);
  const codeRef = useRef(sessionCode);
  codeRef.current = sessionCode;

  const refresh = useCallback(async () => {
    const code = codeRef.current;
    if (!code) return;
    lastAtRef.current = Date.now();
    const { features, ok } = await fetchRoomFeatures(code);
    if (codeRef.current !== code) return; // the session changed while in flight
    setState((prev) => {
      // Before the first good read a failure is all-off (legacy behaviour).
      // After it, a failed read (a network blip on the 60 s poll) keeps the
      // last good flags, so the room layer doesn't vanish mid-action. Only a
      // successful read changes them, including the one after a P0501.
      if (!ok && prev.code === code && prev.good) return prev;
      return { code, features: normalizeFeatures(features), loaded: true, good: ok };
    });
  }, []);

  useEffect(() => {
    setState({ code: sessionCode, features: FEATURES_OFF, loaded: false, good: false });
    if (!enabled || !sessionCode) return undefined;

    refresh();
    const poll = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      refresh();
    }, FEATURE_REFRESH_MS);
    const onFocus = () => {
      if (Date.now() - lastAtRef.current < FOCUS_THROTTLE_MS) return;
      refresh();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') onFocus();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(poll);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [sessionCode, enabled, refresh]);

  const current = state.code === sessionCode && enabled ? state : { features: FEATURES_OFF, loaded: false };
  return { features: current.features, loaded: current.loaded, refresh };
}
